import { getApiErrorDetail } from './getApiErrorDetail'

export type AddProjectUserAction = 'create' | 'existing'

export type MappedAddProjectUserError = {
  message: string
  level: 'error' | 'warning'
  code?: string
}

type ApiErrorBody = {
  detail?: unknown
  message?: unknown
  error?: {
    code?: unknown
    details?: unknown
  }
}

const DEFAULT_EMAIL_EXISTS_MESSAGE =
  'A user with this email already exists. Use Add Existing User instead.'

const SHORT_EMAIL_EXISTS_MESSAGE = 'A user with this email already exists.'

function getResponseData(err: unknown): ApiErrorBody | null {
  if (!err || typeof err !== 'object' || !('response' in err)) return null
  const data = (err as { response?: { data?: unknown } }).response?.data
  if (!data || typeof data !== 'object') return null
  return data as ApiErrorBody
}

function getErrorCode(data: ApiErrorBody): string | null {
  const code = data.error?.code
  return typeof code === 'string' && code.trim() ? code : null
}

function getErrorDetailMessages(data: ApiErrorBody): string[] {
  const details = data.error?.details
  if (!Array.isArray(details)) return []

  return details
    .map(detail => {
      if (typeof detail === 'string') return detail
      if (detail && typeof detail === 'object' && 'message' in detail) {
        const message = (detail as { message?: unknown }).message
        return typeof message === 'string' ? message : null
      }
      return null
    })
    .filter((message): message is string => !!message?.trim())
}

function mapConstraintFallback(
  messages: string[],
): MappedAddProjectUserError | null {
  const joined = messages.join(' ')
  if (joined.includes('uq_project_worker_project_user')) {
    return {
      message: 'This user is already on this project',
      level: 'warning',
      code: 'user_already_on_project',
    }
  }
  if (joined.includes('uq_user_email')) {
    return {
      message: SHORT_EMAIL_EXISTS_MESSAGE,
      level: 'warning',
      code: 'user_email_already_exists',
    }
  }
  return null
}

function getStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object' || !('response' in err)) return null
  const status = (err as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' ? status : null
}

/** Map add/create project-user API errors to toast message + level. */
export function mapAddProjectUserError(
  err: unknown,
  action: AddProjectUserAction,
): MappedAddProjectUserError {
  const data = getResponseData(err)
  const code = data ? getErrorCode(data) : null
  const detailMessages = data ? getErrorDetailMessages(data) : []

  if (code === 'user_already_on_project') {
    return {
      message: 'This user is already on this project',
      level: 'warning',
      code,
    }
  }

  if (code === 'user_email_already_exists') {
    const backendDetail = detailMessages[0]
    return {
      message: backendDetail?.trim() || DEFAULT_EMAIL_EXISTS_MESSAGE,
      level: 'warning',
      code,
    }
  }

  if (data) {
    const constraintMapped = mapConstraintFallback(detailMessages)
    if (constraintMapped) return constraintMapped
  }

  // Prefer top-level detail (e.g. welcome-email 503) over nested integrity strings.
  if (data) {
    const topLevelDetail = data.detail
    if (typeof topLevelDetail === 'string' && topLevelDetail.trim()) {
      return {
        message: topLevelDetail,
        level: 'error',
        code: code ?? undefined,
      }
    }
  }

  const detail = getApiErrorDetail(err)
  if (detail) {
    return { message: detail, level: 'error', code: code ?? undefined }
  }

  const status = getStatus(err)
  if (status === 400) {
    return {
      message:
        action === 'create'
          ? 'Could not create user. Please check the form fields.'
          : 'Could not add user. Please check your input.',
      level: 'error',
    }
  }
  if (status === 404) {
    return { message: 'Project not found', level: 'error' }
  }

  return {
    message:
      action === 'create'
        ? 'Could not create user. Please try again.'
        : 'Could not add user. Please try again.',
    level: 'error',
  }
}
